import { buildFogShape, effectiveVisionRadius, pointInFogShape, type FogWall } from "../../shared/fog.js";
import type { FogVisionMode, MazeDTO, TankDTO } from "../../shared/protocol/index.js";
import type { Renderer } from "./Renderer.js";
import type { FogSource, FogView } from "./types.js";

export function fogView(rd: Renderer, local: TankDTO | null, tanks: TankDTO[]): FogView | null {
  if (!rd.fogOfWar || !local || !rd.maze) return null;
  const visionTanks = [
    local,
    ...(
      local.team >= 0
        ? tanks.filter((t) => t.id !== local.id && t.alive && t.team === local.team)
        : []
    ),
  ];
  const baseRadius = effectiveVisionRadius(rd.visionRadius, rd.maze.width, rd.maze.height);
  const arena = { x: 0, y: 0, width: rd.maze.width, height: rd.maze.height };
  const walls = visionWalls(rd);
  // Scope only benefits its own user: a teammate's scope must not extend or
  // x-ray the local player's fog, so honor `scoped` for the local tank only.
  const sources = visionTanks.map((t) => tankFogSource(rd, t, baseRadius, t.id === local.id, arena, walls));
  for (const z of rd.spawnZones) {
    if (!fogVisionIncludes(z.team, local.team, rd.fogBaseVision)) continue;
    // Bases reveal exactly their own square zone, not a circle around it.
    sources.push(staticRectFogSource(z.x, z.y, z.width, z.height));
  }
  // Flags don't grant vision; instead they're drawn faintly over the fog
  // (gated by fogFlagVision) in drawFlagMarkers so you can spot them.
  return { local, sources };
}

export function fogVisionIncludes(ownerTeam: number, localTeam: number, mode: FogVisionMode): boolean {
  if (mode === "all") return true;
  return mode === "team" && localTeam >= 0 && ownerTeam === localTeam;
}

export function visionWalls(rd: Renderer): readonly FogWall[] {
  if (!rd.maze) return [];
  if (!rd.destructibleWalls) return rd.maze.walls;
  return rd.maze.walls.filter((_, i) => rd.wallHp[i] > 0);
}

function tankFogSource(
  rd: Renderer,
  tank: TankDTO,
  baseRadius: number,
  applyScope: boolean,
  arena: { x: number; y: number; width: number; height: number },
  walls: readonly FogWall[]
): FogSource {
  // Scope only benefits its own user: a teammate's scope must not extend or
  // x-ray the local player's fog, so honor `scoped` for the local tank only.
  const scoped = applyScope && tank.scoped;
  const radius = scoped ? baseRadius * 2 : baseRadius;
  return {
    x: tank.x,
    y: tank.y,
    haloRadius: rd.tankR + 9,
    shape: buildFogShape({ x: tank.x, y: tank.y, radius, seesThroughWalls: scoped, arena, walls }),
  };
}

/** A reveal source shaped as an axis-aligned rectangle (its own square base zone). */
function staticRectFogSource(x: number, y: number, width: number, height: number): FogSource {
  return {
    x: x + width / 2,
    y: y + height / 2,
    haloRadius: 0,
    shape: { kind: "rect", x, y, width, height },
  };
}

export function drawVisionClipped(rd: Renderer, fog: FogView | null, draw: () => void): void {
  if (!fog) {
    draw();
    return;
  }
  const { ctx } = rd;
  ctx.save();
  ctx.beginPath();
  addFogShape(rd, fog, true);
  ctx.clip();
  draw();
  ctx.restore();
}

function addFogShape(rd: Renderer, fog: FogView, includeHalo: boolean, ctx: CanvasRenderingContext2D = rd.ctx): void {
  for (const source of fog.sources) {
    const shape = source.shape;
    if (shape.kind === "rect") {
      ctx.rect(shape.x, shape.y, shape.width, shape.height);
    } else if (shape.kind === "circle") {
      ctx.moveTo(shape.x + shape.radius, shape.y);
      ctx.arc(shape.x, shape.y, shape.radius, 0, Math.PI * 2);
    } else if (shape.points.length > 0) {
      ctx.moveTo(shape.points[0].x, shape.points[0].y);
      for (let i = 1; i < shape.points.length; i++) ctx.lineTo(shape.points[i].x, shape.points[i].y);
      ctx.closePath();
    }
    if (includeHalo && source.haloRadius > 0) {
      ctx.moveTo(source.x + source.haloRadius, source.y);
      ctx.arc(source.x, source.y, source.haloRadius, 0, Math.PI * 2);
    }
  }
}

export function drawFogOverlay(rd: Renderer, fog: FogView, maze: MazeDTO): void {
  const { ctx } = rd;
  const { canvas: overlay, ctx: overlayCtx } = fogOverlayTarget(rd, maze.width, maze.height);
  overlayCtx.clearRect(0, 0, maze.width, maze.height);
  overlayCtx.globalCompositeOperation = "source-over";
  overlayCtx.fillStyle = "#12100e";
  overlayCtx.fillRect(0, 0, maze.width, maze.height);
  overlayCtx.globalCompositeOperation = "destination-out";
  overlayCtx.beginPath();
  addFogShape(rd, fog, true, overlayCtx);
  overlayCtx.fill();
  overlayCtx.globalCompositeOperation = "source-over";
  ctx.save();
  // Destination size in world units — the overlay's own scale maps 1:1 onto
  // the main canvas's backing scale, so vision edges stay crisp.
  ctx.drawImage(overlay, 0, 0, maze.width, maze.height);
  ctx.restore();
}

function fogOverlayTarget(rd: Renderer, width: number, height: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  if (!rd.fogOverlay) {
    rd.fogOverlay = document.createElement("canvas");
    rd.fogOverlayCtx = rd.fogOverlay.getContext("2d");
    if (!rd.fogOverlayCtx) throw new Error("2D canvas context unavailable");
  }
  const pw = Math.max(1, Math.round(width * rd.dpr));
  const ph = Math.max(1, Math.round(height * rd.dpr));
  if (rd.fogOverlay.width !== pw || rd.fogOverlay.height !== ph) {
    rd.fogOverlay.width = pw;
    rd.fogOverlay.height = ph;
  }
  rd.fogOverlayCtx!.setTransform(rd.dpr, 0, 0, rd.dpr, 0, 0);
  return { canvas: rd.fogOverlay, ctx: rd.fogOverlayCtx! };
}

/**
 * Line-of-sight check for fog of war. An enemy is visible when any team reveal
 * source can see it; scoped tank sources skip the wall check (x-ray).
 */
export function isVisible(enemy: TankDTO, fog: FogView): boolean {
  if (fog.local.team >= 0 && enemy.team === fog.local.team) return true;
  if (!isPointVisible(enemy.x, enemy.y, fog)) return false;
  return true;
}

export function isPointVisible(x: number, y: number, fog: FogView): boolean {
  return fog.sources.some((source) => isPointVisibleFromSource(x, y, source));
}

function isPointVisibleFromSource(x: number, y: number, fog: FogSource): boolean {
  const localDx = x - fog.x;
  const localDy = y - fog.y;
  if (localDx * localDx + localDy * localDy <= fog.haloRadius * fog.haloRadius) return true;
  return pointInFogShape(x, y, fog.shape);
}
